import { Connection, PublicKey } from '@solana/web3.js'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Market } from '@cks-systems/manifest-sdk'
import store, { ManifestMarket } from 'stores/store'
import { milliseconds } from 'date-fns'
import { useCallback, useMemo } from 'react'
import { fetchLastPriceForMints } from 'utils/tokens'

export const calcMarketDecimals = (
  price: number,
): [tickDecimals: number, minOrderSizeDecimals: number] => {
  if (price >= 10000) {
    return [1, 6]
  } else if (price >= 1000 && price < 10000) {
    return [2, 5]
  } else if (price >= 100 && price < 1000) {
    return [3, 4]
  } else return [3, 3]
}

async function fetchMarketMintPrices(baseMint: string, quoteMint: string) {
  const prices = await fetchLastPriceForMints([baseMint, quoteMint])

  if (prices?.length !== 2) {
    return null
  }

  const baseMintPrice = prices.find((token) => token.mint === baseMint)?.price
  const quoteMintPrice = prices.find((token) => token.mint === quoteMint)?.price

  return baseMintPrice && quoteMintPrice
    ? {
        [baseMint]: baseMintPrice,
        [quoteMint]: quoteMintPrice,
      }
    : null
}

async function fetchManifestMarket(
  connection: Connection,
  baseMint: string,
  quoteMint: string,
) {
  try {
    const market = (
      await Market.findByMints(
        connection,
        new PublicKey(baseMint),
        new PublicKey(quoteMint),
      )
    )?.[0]

    return market ?? null
  } catch (e) {
    console.error(
      `error fetching manifest market: baseMint=${baseMint}, quoteMint=${quoteMint}`,
      e,
    )
    return null
  }
}

function makeQueryKeys(baseMint: string, quoteMint: string) {
  return {
    fetchMarketMintPrices: [
      'fetch-manifest-market-mint-prices',
      baseMint,
      quoteMint,
    ],
    fetchMarket: ['fetch-manifest-market', baseMint, quoteMint],
  }
}

export function useManifestMarket(
  baseMint: string,
  quoteMint: string,
  enabled: boolean,
) {
  const connection = store((s) => s.connection)
  const queryKeys = makeQueryKeys(baseMint, quoteMint)

  const {
    data: tokenPrices,
    isInitialLoading: initialLoadingMarketMintPrices,
  } = useQuery({
    queryKey: queryKeys.fetchMarketMintPrices,
    queryFn: async () => {
      if (!baseMint || !quoteMint) {
        throw new Error(
          `baseMint and quoteMint are required to fetch manifest market mint prices: baseMint=${baseMint}, quoteMint=${quoteMint}`,
        )
      }

      return fetchMarketMintPrices(baseMint, quoteMint)
    },
    cacheTime: milliseconds({ minutes: 5 }),
    staleTime: milliseconds({ minutes: 10 }),
    retry: 3,
    refetchOnWindowFocus: false,
    enabled: enabled && !!baseMint && !!quoteMint,
  })

  const {
    data: manifestMarket,
    isInitialLoading: initialLoadingManifestMarket,
  } = useQuery({
    queryKey: queryKeys.fetchMarket,
    queryFn: () => {
      if (!baseMint || !quoteMint) {
        throw new Error(
          `baseMint and quoteMint are required to fetch manifest market: baseMint=${baseMint}, quoteMint=${quoteMint}`,
        )
      }
      return fetchManifestMarket(connection, baseMint, quoteMint)
    },
    cacheTime: milliseconds({ minutes: 30 }),
    staleTime: milliseconds({ minutes: 30 }),
    retry: 3,
    refetchOnWindowFocus: false,
    enabled: enabled && !!baseMint && !!quoteMint,
  })

  const extendedManifestMarket = useMemo(() => {
    let tickDecimals = 3
    let minOrderSizeDecimals = 0
    if (manifestMarket) {
      if (tokenPrices) {
        const baseMintPrice = tokenPrices[baseMint] || 0
        const quoteMintPrice = tokenPrices[quoteMint] || 0
        const baseTokenPrice = baseMintPrice / quoteMintPrice
        const maxDecimals = calcMarketDecimals(baseTokenPrice)
        tickDecimals = maxDecimals[0]
        minOrderSizeDecimals = maxDecimals[1]
      }
      return {
        market: manifestMarket,
        tickDecimals,
        minOrderSizeDecimals,
      } satisfies ManifestMarket
    }
  }, [baseMint, manifestMarket, quoteMint, tokenPrices])

  return {
    manifestMarket: extendedManifestMarket,
    initialLoadingManifestMarket:
      initialLoadingMarketMintPrices || initialLoadingManifestMarket,
  }
}

export function useInvalidateManifestMarket() {
  const queryClient = useQueryClient()

  return useCallback(
    async (baseMint: string, quoteMint: string) => {
      if (!baseMint || !quoteMint) return

      return Promise.all(
        Object.values(makeQueryKeys(baseMint, quoteMint)).map((key) =>
          queryClient.invalidateQueries({ queryKey: key }),
        ),
      )
    },
    [queryClient],
  )
}
